import { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { parseExcelFile, ValidationResult, OrgMember, SiteMissingCoordinates } from '@/utils/excelImport';
import { Upload, AlertTriangle, CheckCircle2, XCircle, FileSpreadsheet, Users, MapPin, Info } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { PendingInvitationsReviewDialog } from './PendingInvitationsReviewDialog';
import { SiteCoordinatesReviewDialog } from './SiteCoordinatesReviewDialog';

interface ExcelImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  existingAssets: {
    sites: any[];
    cloudServices: any[];
    zones: any[];
    systems: any[];
    endpoints: any[];
    internetConnections?: any[];
    dmzConfigs?: any[];
    firewallRules?: any[];
  };
  onConfirm: (assets: ValidationResult['preview']) => Promise<Map<string, string>>;
}

export function ExcelImportDialog({
  open,
  onOpenChange,
  orgId,
  existingAssets,
  onConfirm,
}: ExcelImportDialogProps) {
  const { user } = useAuth();
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [showInvitationReview, setShowInvitationReview] = useState(false);
  const [showCoordinatesReview, setShowCoordinatesReview] = useState(false);
  const [sitesWithoutCoordinates, setSitesWithoutCoordinates] = useState<SiteMissingCoordinates[]>([]);
  const [, setImportedAssets] = useState<ValidationResult['preview'] | null>(null);

  // Fetch org members when dialog opens
  useEffect(() => {
    if (open && orgId) {
      fetchOrgMembers();
    }
  }, [open, orgId]);

  const fetchOrgMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, email, name')
        .eq('org_id', orgId);

      if (error) throw error;
      setOrgMembers(data || []);
    } catch (error) {
      console.error('Error fetching org members:', error);
    }
  };

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setIsValidating(true);
    setValidationResult(null);

    try {
      const result = await parseExcelFile(file, existingAssets, orgId, orgMembers);
      setValidationResult(result);
      
      if (result.isValid) {
        if (result.pendingOwners.length > 0) {
          toast.success(`File validated. ${result.pendingOwners.length} new owner(s) will need invitations.`);
        } else {
          toast.success('File validated successfully');
        }
      } else {
        toast.error(`Validation failed with ${result.errors.length} error(s)`);
      }
    } catch (error) {
      console.error('Validation error:', error);
      toast.error(`Failed to validate file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsValidating(false);
    }

    event.target.value = '';
  }, [existingAssets, orgId, orgMembers]);

  const handleConfirmImport = async () => {
    if (!validationResult?.isValid || !validationResult.preview) return;

    setIsImporting(true);
    try {
      // Create pending owner assignments in database
      if (validationResult.pendingOwners.length > 0) {
        await createPendingOwnerAssignments(validationResult);
      }

      // Perform the actual import and get the ID mapping
      const idMapping = await onConfirm(validationResult.preview);
      setImportedAssets(validationResult.preview);
      
      // Get the actual site IDs after import using the returned idMapping
      const importedSiteIds = validationResult.sitesWithoutCoordinates.map(s => {
        // For existing sites, use the existing ID directly
        if (s._isExisting) {
          return { ...s, id: s.id };
        }
        // For new sites, resolve temp ID to real ID using the mapping
        const realId = s._tempId ? idMapping.get(s._tempId) : s.id;
        return { ...s, id: realId || s.id };
      }).filter(s => s.id && !s.id.startsWith('temp_')); // Only keep sites with real IDs
      
      setSitesWithoutCoordinates(importedSiteIds);
      
      // Determine which dialog to show next
      if (validationResult.pendingOwners.length > 0) {
        setShowInvitationReview(true);
        toast.success('Assets imported! Review pending owner invitations.');
      } else if (importedSiteIds.length > 0) {
        setShowCoordinatesReview(true);
        toast.success('Assets imported! Set GPS coordinates for imported sites.');
      } else {
        toast.success('Assets imported successfully');
        handleClose();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import assets');
    } finally {
      setIsImporting(false);
    }
  };

  const createPendingOwnerAssignments = async (result: ValidationResult) => {
    // Create pending owner assignments for each pending owner
    const assignments: Array<{
      org_id: string;
      entity_type: string;
      entity_id: string;
      pending_email: string;
      created_by: string | undefined;
    }> = [];

    // Collect all pending assignments from sites
    result.preview.sites.forEach(site => {
      if (site._pendingOwnerEmail && site.id) {
        assignments.push({
          org_id: orgId,
          entity_type: 'site',
          entity_id: site.id,
          pending_email: site._pendingOwnerEmail,
          created_by: user?.id,
        });
      }
    });

    // Collect from systems
    result.preview.systems.forEach(system => {
      if (system._pendingOwnerEmail && system.id) {
        assignments.push({
          org_id: orgId,
          entity_type: 'system',
          entity_id: system.id,
          pending_email: system._pendingOwnerEmail,
          created_by: user?.id,
        });
      }
    });

    if (assignments.length > 0) {
      const { error } = await supabase
        .from('pending_owner_assignments')
        .insert(assignments);

      if (error) {
        console.error('Error creating pending assignments:', error);
        // Don't throw - this is not critical to the import
      }
    }
  };

  const handleClose = () => {
    setValidationResult(null);
    setSelectedFile(null);
    setImportedAssets(null);
    setSitesWithoutCoordinates([]);
    onOpenChange(false);
  };

  const handleInvitationReviewClose = () => {
    setShowInvitationReview(false);
    // Check if we need to show coordinates dialog next
    if (sitesWithoutCoordinates.length > 0) {
      setShowCoordinatesReview(true);
    } else {
      handleClose();
    }
  };

  const handleCoordinatesReviewComplete = () => {
    setShowCoordinatesReview(false);
    handleClose();
  };

  return (
    <>
      <Dialog open={open && !showInvitationReview && !showCoordinatesReview} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Import Assets from Excel
            </DialogTitle>
            <DialogDescription>
              Upload an Excel file to import assets. The file will be validated before import.
            </DialogDescription>
          </DialogHeader>

          {/* File Upload - Fixed at top */}
          <div className="border-2 border-dashed rounded-lg p-6 text-center flex-shrink-0">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              id="excel-upload"
              disabled={isValidating || isImporting}
            />
            <label htmlFor="excel-upload" className="cursor-pointer">
              <Upload className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm font-medium">
                {selectedFile ? selectedFile.name : 'Click to upload Excel file'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Supports .xlsx and .xls formats
              </p>
            </label>
          </div>

          {/* Validation in progress */}
          {isValidating && (
            <Alert className="flex-shrink-0">
              <AlertDescription>Validating file...</AlertDescription>
            </Alert>
          )}

          {/* Validation Results - Scrollable */}
          {validationResult && (
            <ScrollArea className="flex-1 min-h-0 max-h-[50vh]">
              <div className="space-y-4 pr-4">
                {/* Summary with new/existing breakdown */}
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { key: 'sites', label: 'Sites' },
                    { key: 'cloudServices', label: 'Cloud' },
                    { key: 'zones', label: 'Zones' },
                    { key: 'systems', label: 'Systems' },
                    { key: 'endpoints', label: 'Endpoints' },
                  ].map(({ key, label }) => {
                    const summary = validationResult.summary[key as keyof typeof validationResult.summary];
                    return (
                      <div key={key} className="rounded-lg border p-3 text-center">
                        <div className="text-2xl font-bold">{summary.total}</div>
                        <div className="text-xs text-muted-foreground">{label}</div>
                        {summary.total > 0 && (
                          <div className="text-xs mt-1 space-x-1">
                            {summary.new > 0 && (
                              <span className="text-green-600 dark:text-green-400">+{summary.new} new</span>
                            )}
                            {summary.existing > 0 && (
                              <span className="text-blue-600 dark:text-blue-400">{summary.existing} update</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* ERRORS FIRST - Show prominently when there are errors */}
                {validationResult.errors.length > 0 && (
                  <div className="space-y-2">
                    <Alert variant="danger">
                      <XCircle className="h-4 w-4" />
                      <AlertDescription>
                        Found {validationResult.errors.length} error(s). Please fix them before importing:
                      </AlertDescription>
                    </Alert>
                    <ScrollArea className="h-[200px] rounded-lg border border-destructive/50 bg-destructive/5">
                      <div className="p-3 space-y-2">
                        {validationResult.errors.map((error, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm p-2 rounded bg-background/50 border-b last:border-0">
                            <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <Badge variant="outline" className="mr-2 text-xs">{error.sheet}</Badge>
                              <span className="text-muted-foreground">Row {error.row}:</span>
                              <span className="ml-1 font-medium text-foreground">{error.column}</span>
                              <span className="ml-2 text-destructive">- {error.message}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* Success Status - Only show when valid */}
                {validationResult.isValid && (
                  <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800 dark:text-green-200">
                      {(() => {
                        const totalNew = Object.values(validationResult.summary).reduce((sum, s) => sum + s.new, 0);
                        const totalExisting = Object.values(validationResult.summary).reduce((sum, s) => sum + s.existing, 0);
                        if (totalNew === 0) return 'No new assets to import - all items already exist.';
                        if (totalExisting > 0) return `Ready to import ${totalNew} new item(s). ${totalExisting} existing item(s) will be skipped.`;
                        return `Ready to import ${totalNew} new item(s).`;
                      })()}
                    </AlertDescription>
                  </Alert>
                )}

                {/* NOTICES & WARNINGS - Consolidated scrollable section */}
                {(validationResult.pendingOwners.length > 0 || 
                  validationResult.sitesWithoutCoordinates.length > 0 || 
                  validationResult.warnings.length > 0) && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                      <Info className="h-4 w-4" />
                      <span className="font-medium">
                        {validationResult.pendingOwners.length + validationResult.sitesWithoutCoordinates.length + validationResult.warnings.length} notice(s) - import can still proceed
                      </span>
                    </div>
                    <ScrollArea className="h-[200px] rounded-lg border border-blue-500/30 bg-blue-50/50 dark:bg-blue-950/20">
                      <div className="p-3 space-y-3">
                        {/* Pending Owners */}
                        {validationResult.pendingOwners.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300">
                              <Users className="h-4 w-4" />
                              <span>{validationResult.pendingOwners.length} new owner(s) to invite</span>
                            </div>
                            {validationResult.pendingOwners.map(po => (
                              <div key={po.email} className="flex items-start gap-2 text-sm pl-6">
                                <span className="text-foreground">{po.email}</span>
                                <span className="text-muted-foreground">→ {po.entities.length} asset(s)</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Sites Without Coordinates */}
                        {validationResult.sitesWithoutCoordinates.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300">
                              <MapPin className="h-4 w-4" />
                              <span>{validationResult.sitesWithoutCoordinates.length} site(s) without GPS coordinates</span>
                            </div>
                            {validationResult.sitesWithoutCoordinates.map(site => (
                              <div key={site.id} className="flex items-start gap-2 text-sm pl-6">
                                <span className="text-foreground">{site.name}</span>
                                {site.address && <span className="text-muted-foreground">({site.address})</span>}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Validation Warnings */}
                        {validationResult.warnings.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-yellow-700 dark:text-yellow-300">
                              <AlertTriangle className="h-4 w-4" />
                              <span>{validationResult.warnings.length} validation warning(s)</span>
                            </div>
                            {validationResult.warnings.map((warning, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-sm pl-6">
                                <Badge variant="outline" className="text-xs flex-shrink-0">{warning.sheet}</Badge>
                                <span className="text-muted-foreground">Row {warning.row}:</span>
                                <span className="font-medium text-foreground">{warning.column}</span>
                                <span className="text-muted-foreground">- {warning.message}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}

          {/* Actions - Fixed at bottom */}
          <div className="flex justify-end gap-2 pt-4 border-t flex-shrink-0">
            <Button variant="outline" onClick={handleClose} disabled={isImporting}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmImport}
              disabled={!validationResult?.isValid || isImporting}
            >
              {isImporting ? 'Importing...' : 
                (validationResult?.pendingOwners?.length || validationResult?.sitesWithoutCoordinates?.length)
                  ? 'Import & Review'
                  : 'Confirm Import'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pending Invitations Review Dialog */}
      {showInvitationReview && validationResult && (
        <PendingInvitationsReviewDialog
          open={showInvitationReview}
          onOpenChange={handleInvitationReviewClose}
          pendingOwners={validationResult.pendingOwners}
          orgId={orgId}
        />
      )}

      {/* Site Coordinates Review Dialog */}
      {showCoordinatesReview && sitesWithoutCoordinates.length > 0 && (
        <SiteCoordinatesReviewDialog
          open={showCoordinatesReview}
          onOpenChange={(open) => !open && handleCoordinatesReviewComplete()}
          sitesWithoutCoordinates={sitesWithoutCoordinates}
          onComplete={handleCoordinatesReviewComplete}
        />
      )}
    </>
  );
}
